// src/components/admin/CircuitReservationAdminModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Route,
  MapPin,
  Calendar,
  Clock,
  Users,
  Mail,
  Phone,
  Euro,
  Link2,
  Send,
  Star,
  Image as ImageIcon,
  Info,
  CheckCircle2,
  Building2,
  BedDouble,
} from "lucide-react";
import adminService from "../../services/adminService";

/* =========================
   Helpers
========================= */
const pick = (obj, paths, fallback = null) => {
  for (const p of paths) {
    const v = p.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

const normalizeId = (v) => {
  // populate { _id }
  if (v && typeof v === "object" && v._id) return String(v._id);
  // mongo export { $oid }
  if (v && typeof v === "object" && v.$oid) return String(v.$oid);
  // string
  if (typeof v === "string") return v;
  return "";
};

const formatDateFR = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString("fr-FR");
  } catch {
    return String(raw);
  }
};

const formatMoneyMAD = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  try {
    return new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD" }).format(x);
  } catch {
    return `${x} MAD`;
  }
};

const safeArr = (x) => (Array.isArray(x) ? x : []);

const isHttp = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

/* =========================
   Component
========================= */
const CircuitReservationAdminModal = ({ reservation, onClose, onUpdated }) => {
  const reservationId = reservation?._id;

  // ✅ API base (pour fetch hôtels direct)
  const API_BASE =
    (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");

  /* ---------- state ---------- */
  const [prixFinal, setPrixFinal] = useState(
    reservation?.prixFinal || reservation?.prix_total || reservation?.prixTotal || ""
  );
  const [paymentUrl, setPaymentUrl] = useState(
    reservation?.paymentUrl || reservation?.paiementUrl || ""
  );

  const [assignedCircuit, setAssignedCircuit] = useState(null);
  const [loadingCircuit, setLoadingCircuit] = useState(false);

  const [includedHotels, setIncludedHotels] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(false);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  /* =========================
     Reservation fields
  ========================= */
  const destination = pick(reservation, ["destination", "demande.destination"], "—");
  const stations = pick(reservation, ["itinerary.stations", "stations", "itineraire.stations"], []);

  const travelDateRaw = pick(reservation, ["travelDate", "dateDebut", "date"], "");
  const travelDate = useMemo(() => formatDateFR(travelDateRaw), [travelDateRaw]);

  const duree = pick(reservation, ["duree", "duration"], "—");
  const travelType = pick(reservation, ["travelType", "typeVoyage"], "—");
  const nombrePersonnes = pick(reservation, ["nombrePersonnes", "participants"], null);
  const prixTotal = pick(reservation, ["prixTotal", "prix_total"], null);
  const acceptedCmi = !!pick(reservation, ["acceptedCmi", "acceptCgv"], false);
  const statut = pick(reservation, ["statut", "status"], "—");
  const reference = pick(reservation, ["reference"], "—");

  const createdAt = useMemo(() => formatDateFR(pick(reservation, ["createdAt"], "")), [reservation]);
  const updatedAt = useMemo(() => formatDateFR(pick(reservation, ["updatedAt"], "")), [reservation]);

  const budget = useMemo(() => {
    const min = pick(reservation, ["budget.min", "minPrice", "prixMin"], null);
    const max = pick(reservation, ["budget.max", "maxPrice", "prixMax"], null);
    if (min == null && max == null) return "—";
    // budget en MAD dans ton projet, donc on affiche MAD
    return `${min ?? "—"} MAD → ${max ?? "—"} MAD`;
  }, [reservation]);

  /* =========================
     Client (fallback sur champs top-level)
  ========================= */
  const client = useMemo(() => {
    const fullFromClient =
      pick(reservation, ["client.fullName", "client.nomComplet", "client.username"], "") ||
      `${reservation?.client?.prenom || ""} ${reservation?.client?.nom || ""}`.trim();

    return {
      fullName: fullFromClient || pick(reservation, ["fullName"], "—"),
      email: pick(reservation, ["client.email", "email"], "—"),
      phone: pick(reservation, ["client.telephone", "client.phone", "telephone", "phone"], "—"),
      address: pick(reservation, ["client.adresse", "client.address", "adresse", "address"], "—"),
    };
  }, [reservation]);

  /* =========================
     Circuit ID
  ========================= */
  const circuitId = useMemo(() => {
    const c = reservation?.circuit;
    const id = normalizeId(c) || normalizeId(reservation?.circuitId);
    return id || "";
  }, [reservation]);

  /* =========================
     Load Circuit by ID
  ========================= */
  useEffect(() => {
    const loadCircuit = async () => {
      if (!circuitId) {
        setAssignedCircuit(null);
        return;
      }
      try {
        setLoadingCircuit(true);
        const res = await adminService.getCircuitById(circuitId);

        // selon ton backend : res peut être {data:{...}} ou {data:{data:{...}}} ou objet direct
        const c =
          res?.data?.data ||
          res?.data ||
          res;

        setAssignedCircuit(c || null);
      } catch (e) {
        console.warn("getCircuitById failed:", e);
        setAssignedCircuit(null);
      } finally {
        setLoadingCircuit(false);
      }
    };
    loadCircuit();
  }, [circuitId]);

  /* =========================
     Load Hotels included (from circuit.etapes[].hotel)
  ========================= */
  useEffect(() => {
    const loadHotels = async () => {
      const etapes = safeArr(assignedCircuit?.etapes);

      const hotelIds = etapes
        .map((e) => normalizeId(e?.hotel))
        .filter(Boolean);

      if (!hotelIds.length) {
        setIncludedHotels([]);
        return;
      }

      try {
        setLoadingHotels(true);

        const results = await Promise.all(
          hotelIds.map(async (hid) => {
            try {
              const r = await fetch(`${API_BASE}/hotels/${hid}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
              });
              const data = await r.json().catch(() => ({}));
              // backend peut renvoyer {hotel: {...}} ou {...}
              return data?.hotel || data?.data || data;
            } catch {
              return null;
            }
          })
        );

        // on garde unique + nettoie null
        const cleaned = results.filter(Boolean);
        setIncludedHotels(cleaned);
      } finally {
        setLoadingHotels(false);
      }
    };

    loadHotels();
  }, [assignedCircuit, API_BASE]);

  /* =========================
     Sync reservation → inputs
  ========================= */
  useEffect(() => {
    setPrixFinal(
      reservation?.prixFinal || reservation?.prix_total || reservation?.prixTotal || ""
    );
    setPaymentUrl(reservation?.paymentUrl || reservation?.paiementUrl || "");
  }, [reservation]);

  /* =========================
     Admin Actions (keep)
  ========================= */
  const finalizePrice = async () => {
    try {
      setErr("");
      setMsg("");
      if (!prixFinal || Number(prixFinal) <= 0) {
        setErr("Veuillez saisir un prix final valide.");
        return;
      }
      setLoading(true);
      await adminService.updateCircuitReservationPrice(reservationId, Number(prixFinal));
      setMsg("Prix final enregistré.");
      onUpdated?.();
    } catch {
      setErr("Erreur lors de l’enregistrement du prix final.");
    } finally {
      setLoading(false);
    }
  };

  const generatePaymentLink = async () => {
    try {
      setErr("");
      setMsg("");
      if (!prixFinal || Number(prixFinal) <= 0) {
        setErr("Veuillez d’abord enregistrer un prix final.");
        return;
      }
      setLoading(true);
      const res = await adminService.generateCircuitReservationPaymentLink(reservationId, Number(prixFinal));
      const url =
        res?.data?.data?.paymentUrl ||
        res?.data?.paymentUrl ||
        res?.data?.url ||
        "";
      if (!url) throw new Error("no url");
      setPaymentUrl(url);
      setMsg("Lien de paiement généré.");
      onUpdated?.();
    } catch {
      setErr("Erreur lors de la génération du lien de paiement.");
    } finally {
      setLoading(false);
    }
  };

  const sendPaymentEmail = async () => {
    try {
      setErr("");
      setMsg("");
      if (!paymentUrl) {
        setErr("Générez d’abord le lien de paiement.");
        return;
      }
      setLoading(true);
      await adminService.sendCircuitReservationPaymentEmail(reservationId);
      setMsg("Email envoyé au client.");
      onUpdated?.();
    } catch {
      setErr("Erreur lors de l’envoi de l’email.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Circuit derived
  ========================= */
  const circuitTitre = assignedCircuit?.titre || "—";
  const circuitDuree = assignedCircuit?.duree || "—";
  const circuitPrix = assignedCircuit?.prix != null ? formatMoneyMAD(assignedCircuit.prix) : "—";
  const circuitNote = assignedCircuit?.note ?? null;
  const circuitAvis = assignedCircuit?.avis ?? null;
  const circuitParticipants = assignedCircuit?.participants || null;
  const circuitImage = assignedCircuit?.image || "";
  const circuitDesc = assignedCircuit?.description || "";
  const circuitVilles = safeArr(assignedCircuit?.villes);
  const circuitHighlights = safeArr(assignedCircuit?.highlights);
  const circuitGalerie = safeArr(assignedCircuit?.galerie);
  const circuitInclus =
    assignedCircuit?.inclus && typeof assignedCircuit.inclus === "object" ? assignedCircuit.inclus : null;
  const circuitEtapes = safeArr(assignedCircuit?.etapes);

  /* =========================
     Render
  ========================= */
  return (
    <div className="fixed inset-0 z-[90]">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* scroll container */}
      <div className="relative h-full w-full overflow-y-auto px-4 py-6">
        <div className="mx-auto w-full max-w-5xl">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border">
            {/* HEADER (sticky) */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Route className="w-5 h-5" />
                  Réservation circuit — détails complets
                </h3>
                <p className="text-xs text-white/90 mt-1">
                  Réf: <span className="font-mono">{reference}</span> • Statut:{" "}
                  <span className="font-semibold">{statut}</span>
                </p>
                <p className="text-xs text-white/80 mt-1">
                  ID : <span className="font-mono">{reservationId}</span>
                </p>
              </div>

              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* BODY (scroll inside) */}
            <div className="p-6 max-h-[78vh] overflow-y-auto space-y-6">
              {err && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3 text-red-700 rounded">
                  {err}
                </div>
              )}
              {msg && (
                <div className="bg-green-50 border-l-4 border-green-600 p-3 text-green-800 rounded">
                  {msg}
                </div>
              )}

              {/* ================= DEMANDE / RÉSERVATION ================= */}
              <div className="border rounded-2xl p-4">
                <h4 className="font-bold mb-3">Détails réservation</h4>

                <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-800">
                  <div className="flex gap-2 items-center">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Destination :</span> {destination}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Calendar className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Date début :</span> {travelDate}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Durée :</span> {duree}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Type :</span> {travelType}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Personnes :</span>{" "}
                    {nombrePersonnes != null ? nombrePersonnes : "—"}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Euro className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Prix total :</span>{" "}
                    {prixTotal != null ? formatMoneyMAD(prixTotal) : "—"}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Euro className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">Budget :</span> {budget}
                  </div>

                  <div className="flex gap-2 items-center">
                    <CheckCircle2 className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold">CMI accepté :</span>{" "}
                    {acceptedCmi ? "Oui" : "Non"}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Info className="w-4 h-4 text-slate-500" />
                    <span className="font-semibold">Créé le :</span> {createdAt}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Info className="w-4 h-4 text-slate-500" />
                    <span className="font-semibold">Mis à jour :</span> {updatedAt}
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase mb-2 text-slate-500">Stations</p>
                  {safeArr(stations).length ? (
                    <div className="flex flex-wrap gap-2">
                      {stations.map((s) => (
                        <span
                          key={s}
                          className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-sm"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Aucune station.</p>
                  )}
                </div>

                {pick(reservation, ["commentaires"], "") ? (
                  <div className="mt-4 rounded-xl border bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase mb-1 text-slate-500">Message client</p>
                    <p className="text-sm text-slate-700">
                      {pick(reservation, ["commentaires"], "")}
                    </p>
                  </div>
                ) : null}
              </div>

              {/* ================= CLIENT ================= */}
              <div className="border rounded-2xl p-4">
                <h4 className="font-bold mb-3">Informations client</h4>

                <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-800">
                  <div className="flex gap-2 items-center">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold">Nom :</span> {client.fullName}
                  </div>
                  <div className="flex gap-2 items-center">
                    <Mail className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold">Email :</span> {client.email}
                  </div>
                  <div className="flex gap-2 items-center">
                    <Phone className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold">Téléphone :</span> {client.phone}
                  </div>
                  <div className="flex gap-2 items-center">
                    <MapPin className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold">Adresse :</span> {client.address}
                  </div>
                </div>
              </div>

              {/* ================= CIRCUIT ================= */}
              <div className="border rounded-2xl p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="font-bold">Circuit réservé</h4>
                  {circuitId ? (
                    <span className="text-xs text-slate-500">
                      Circuit ID : <span className="font-mono">{circuitId}</span>
                    </span>
                  ) : null}
                </div>

                {loadingCircuit ? (
                  <p className="text-sm text-slate-500">Chargement du circuit…</p>
                ) : !assignedCircuit ? (
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <Info className="w-4 h-4" /> Aucun circuit trouvé.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* top row */}
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="w-full md:w-[280px]">
                        <div className="rounded-2xl overflow-hidden border bg-slate-50">
                          {circuitImage ? (
                            <img
                              src={circuitImage}
                              alt={circuitTitre}
                              className="w-full h-[180px] object-cover"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          ) : (
                            <div className="h-[180px] flex items-center justify-center text-slate-400">
                              <ImageIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>

                        {circuitGalerie.length > 0 && (
                          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                            {circuitGalerie.slice(0, 8).map((g) => (
                              <div
                                key={g}
                                className="shrink-0 w-20 h-14 rounded-xl overflow-hidden border bg-slate-50"
                              >
                                <img
                                  src={g}
                                  alt="galerie"
                                  className="w-full h-full object-cover"
                                  onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                              </div>
                            ))}
                            {circuitGalerie.length > 8 && (
                              <div className="shrink-0 w-20 h-14 rounded-xl border bg-slate-50 flex items-center justify-center text-xs text-slate-500">
                                +{circuitGalerie.length - 8}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h5 className="text-lg font-bold text-slate-900">{circuitTitre}</h5>
                          {assignedCircuit?.disponible === true && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Disponible
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2 text-sm">
                          <span className="px-3 py-1 rounded-full bg-slate-50 border text-slate-700">
                            <span className="font-semibold">Durée:</span> {circuitDuree}
                          </span>
                          <span className="px-3 py-1 rounded-full bg-slate-50 border text-slate-700">
                            <span className="font-semibold">Prix:</span> {circuitPrix}
                          </span>
                          {circuitParticipants && (
                            <span className="px-3 py-1 rounded-full bg-slate-50 border text-slate-700">
                              <span className="font-semibold">Participants:</span> {circuitParticipants}
                            </span>
                          )}
                          {circuitNote != null && (
                            <span className="px-3 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-800 inline-flex items-center gap-1">
                              <Star className="w-4 h-4" /> {circuitNote}
                              {circuitAvis != null ? (
                                <span className="text-yellow-700">({circuitAvis} avis)</span>
                              ) : null}
                            </span>
                          )}
                        </div>

                        {circuitVilles.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold uppercase mb-2 text-slate-500">Villes</p>
                            <div className="flex flex-wrap gap-2">
                              {circuitVilles.map((v) => (
                                <span
                                  key={v}
                                  className="px-3 py-1 rounded-full text-sm bg-indigo-50 text-indigo-700 border border-indigo-200"
                                >
                                  {v}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {circuitDesc && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold uppercase mb-2 text-slate-500">Description</p>
                            <p className="text-sm text-slate-700 leading-relaxed">{circuitDesc}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* inclus */}
                    {circuitInclus && (
                      <div className="rounded-2xl border p-4">
                        <p className="text-sm font-bold text-slate-900 mb-3">Inclus</p>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          {circuitInclus.transport && (
                            <div className="p-3 rounded-xl border bg-white">
                              <p className="font-semibold text-slate-900 mb-1">Transport</p>
                              <p className="text-slate-700">{circuitInclus.transport}</p>
                            </div>
                          )}
                          {circuitInclus.hebergement && (
                            <div className="p-3 rounded-xl border bg-white">
                              <p className="font-semibold text-slate-900 mb-1">Hébergement</p>
                              <p className="text-slate-700">{circuitInclus.hebergement}</p>
                            </div>
                          )}
                          {circuitInclus.visite && (
                            <div className="p-3 rounded-xl border bg-white">
                              <p className="font-semibold text-slate-900 mb-1">Visites</p>
                              <p className="text-slate-700">{circuitInclus.visite}</p>
                            </div>
                          )}
                          {circuitInclus.activites && (
                            <div className="p-3 rounded-xl border bg-white">
                              <p className="font-semibold text-slate-900 mb-1">Activités</p>
                              <p className="text-slate-700">{circuitInclus.activites}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* highlights */}
                    {circuitHighlights.length > 0 && (
                      <div className="rounded-2xl border p-4 bg-slate-50">
                        <p className="text-sm font-bold text-slate-900 mb-2">Highlights</p>
                        <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-700">
                          {circuitHighlights.map((h) => (
                            <li key={h} className="flex items-start gap-2">
                              <span className="mt-1 w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                              <span>{h}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* ================= HÔTELS INCLUS ================= */}
                    <div className="rounded-2xl border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-orange-600" />
                          Hôtels inclus (selon étapes)
                        </p>
                        <span className="text-xs text-slate-500">
                          Étapes: <span className="font-semibold">{circuitEtapes.length}</span>
                        </span>
                      </div>

                      <div className="mt-3">
                        {loadingHotels ? (
                          <p className="text-sm text-slate-500">Chargement des hôtels…</p>
                        ) : includedHotels.length === 0 ? (
                          <p className="text-sm text-slate-500">
                            Aucun hôtel trouvé (vérifie les IDs dans <code className="text-xs">circuit.etapes[].hotel</code>).
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {includedHotels.map((h) => {
                              const hid = normalizeId(h?._id);
                              const htitre = h?.nom || h?.titre || h?.name || "Hôtel";
                              const hville = h?.ville || h?.city || "";
                              const hadresse = h?.adresse || h?.address || "";
                              const hetoiles = h?.etoiles ?? h?.stars ?? null;

                              const himg =
                                (typeof h?.image === "string" && isHttp(h.image) && h.image) ||
                                (typeof h?.cover === "string" && isHttp(h.cover) && h.cover) ||
                                (Array.isArray(h?.images) && h.images.find((x) => isHttp(x))) ||
                                "";

                              return (
                                <div key={hid || htitre} className="rounded-2xl border bg-white overflow-hidden">
                                  <div className="h-36 bg-slate-100">
                                    {himg ? (
                                      <img
                                        src={himg}
                                        alt={htitre}
                                        className="w-full h-full object-cover"
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                      />
                                    ) : (
                                      <div className="h-full flex items-center justify-center text-slate-400">
                                        <ImageIcon className="w-6 h-6" />
                                      </div>
                                    )}
                                  </div>

                                  <div className="p-4 space-y-2">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="font-bold text-slate-900 truncate">{htitre}</div>
                                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                                          <MapPin className="w-3.5 h-3.5 text-orange-600" />
                                          <span className="truncate">
                                            {[hville, hadresse].filter(Boolean).join(" • ") || "—"}
                                          </span>
                                        </div>
                                      </div>

                                      {hetoiles != null ? (
                                        <div className="shrink-0 text-xs px-2 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-800 inline-flex items-center gap-1">
                                          <Star className="w-3.5 h-3.5" />
                                          {hetoiles}
                                        </div>
                                      ) : null}
                                    </div>

                                    
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Étapes (ville -> hotelId) */}
                      {circuitEtapes.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase mb-2 text-slate-500">Étapes</p>
                          <div className="grid md:grid-cols-2 gap-2 text-sm">
                            {circuitEtapes.map((e, idx) => {
                              const ville = e?.ville || "—";
                              const hid = normalizeId(e?.hotel) || "—";
                              return (
                                <div key={`${ville}-${idx}`} className="rounded-xl border bg-slate-50 p-3 flex items-start gap-3">
                                  <BedDouble className="w-4 h-4 text-orange-600 mt-0.5" />
                                  <div className="min-w-0">
                                    <div className="font-semibold text-slate-900">{ville}</div>
                                    <div className="text-xs text-slate-600 break-all">
                                      Hôtel ID: <span className="font-mono">{hid}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ================= ACTIONS ADMIN ================= */}
              <div className="border rounded-2xl p-4">
                <h4 className="font-bold mb-3">Actions admin</h4>

                <div className="grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prix final (MAD)</label>
                    <input
                      type="number"
                      value={prixFinal}
                      onChange={(e) => setPrixFinal(e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Ex: 2500"
                    />
                  </div>

                  <button
                    onClick={finalizePrice}
                    disabled={loading}
                    className={`rounded-xl px-4 py-3 font-semibold border flex items-center justify-center gap-2 ${
                      loading ? "bg-slate-200 text-slate-500" : "bg-white hover:bg-slate-50 text-slate-900"
                    }`}
                  >
                    <Euro className="w-4 h-4" />
                    Enregistrer prix
                  </button>

                  <button
                    onClick={generatePaymentLink}
                    disabled={loading}
                    className={`rounded-xl px-4 py-3 font-semibold flex items-center justify-center gap-2 ${
                      loading ? "bg-slate-200 text-slate-500" : "bg-orange-600 hover:bg-orange-700 text-white"
                    }`}
                  >
                    <Link2 className="w-4 h-4" />
                    Générer lien CMI
                  </button>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Lien de paiement</label>
                    <div className="flex gap-2">
                      <input
                        value={paymentUrl || ""}
                        readOnly
                        className="flex-1 px-4 py-3 border rounded-xl bg-slate-50"
                        placeholder="Aucun lien généré"
                      />
                      {paymentUrl ? (
                        <a
                          href={paymentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-3 rounded-xl border bg-white hover:bg-slate-50 font-semibold"
                        >
                          Ouvrir
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <button
                    onClick={sendPaymentEmail}
                    disabled={loading || !paymentUrl}
                    className={`md:col-span-3 rounded-xl px-4 py-3 font-semibold flex items-center justify-center gap-2 ${
                      loading || !paymentUrl
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white"
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    Envoyer email au client
                  </button>

                  {!paymentUrl && (
                    <p className="md:col-span-3 text-xs text-slate-500">
                      Pour envoyer l’email, générez d’abord le lien de paiement.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER (sticky) */}
            <div className="sticky bottom-0 bg-slate-50 p-4 flex justify-end border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-xl bg-white hover:bg-slate-100"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitReservationAdminModal;
